import { Children, Fragment, forwardRef } from "react";
import { cn } from "../lib/cn";

/** MUI theme spacing units → Tailwind gap classes (0.5 = 4px, 1 = 8px …). */
const gapBySpacing: Record<NonNullable<StackProps["spacing"]>, string> = {
  0: "gap-0",
  0.5: "gap-1",
  1: "gap-2",
  1.5: "gap-3",
  2: "gap-4",
  2.5: "gap-5",
  3: "gap-6",
  4: "gap-8",
  5: "gap-10",
  6: "gap-12",
};

const flexDirection: Record<NonNullable<StackProps["direction"]>, string> = {
  row: "flex-row",
  "row-reverse": "flex-row-reverse",
  column: "flex-col",
  "column-reverse": "flex-col-reverse",
};

const alignItemsClass: Record<NonNullable<StackProps["alignItems"]>, string> = {
  "flex-start": "items-start",
  center: "items-center",
  "flex-end": "items-end",
  stretch: "items-stretch",
  baseline: "items-baseline",
};

const justifyContentClass: Record<
  NonNullable<StackProps["justifyContent"]>,
  string
> = {
  "flex-start": "justify-start",
  center: "justify-center",
  "flex-end": "justify-end",
  "space-between": "justify-between",
  "space-around": "justify-around",
  "space-evenly": "justify-evenly",
};

export interface StackProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Flex direction. MUI-compatible, defaults to column. */
  direction?: "row" | "row-reverse" | "column" | "column-reverse";
  /** Gap between children in MUI theme units. */
  spacing?: 0 | 0.5 | 1 | 1.5 | 2 | 2.5 | 3 | 4 | 5 | 6;
  alignItems?: "flex-start" | "center" | "flex-end" | "stretch" | "baseline";
  justifyContent?:
    | "flex-start"
    | "center"
    | "flex-end"
    | "space-between"
    | "space-around"
    | "space-evenly";
  /** Rendered between each pair of children (e.g. a Divider). */
  divider?: React.ReactNode;
  /** Accepted for MUI compatibility — gap is always used. */
  useFlexGap?: boolean;
}

/** Interleave the divider node between the array children. */
function renderWithDividers(
  children: React.ReactNode,
  divider: React.ReactNode,
): React.ReactNode[] {
  return Children.toArray(children).map((child, index) => (
    <Fragment key={index}>
      {index > 0 ? divider : null}
      {child}
    </Fragment>
  ));
}

/** Flex layout with a MUI Stack-compatible API. */
export const Stack = forwardRef<HTMLDivElement, StackProps>(
  (
    {
      direction = "column",
      spacing = 0,
      alignItems,
      justifyContent,
      divider,
      useFlexGap: _useFlexGap,
      className,
      children,
      ...props
    },
    ref,
  ) => {
    const content =
      divider === undefined || divider === null
        ? children
        : renderWithDividers(children, divider);

    return (
      <div
        ref={ref}
        className={cn(
          "flex",
          flexDirection[direction],
          gapBySpacing[spacing],
          alignItems && alignItemsClass[alignItems],
          justifyContent && justifyContentClass[justifyContent],
          className,
        )}
        {...props}
      >
        {content}
      </div>
    );
  },
);
Stack.displayName = "Stack";
